import { request } from '@/functions/data-access';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronUp,
  faCopy,
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
} from '@fortawesome/sharp-solid-svg-icons';
import { SCLoading, SCView } from '@lib/sc-eone-ui';
import { useQuery } from '@tanstack/react-query';
import { Button } from 'eone-ui/buttons/button';
import { TabGroup } from 'eone-ui/tab/tab';
import { useEffect, useState } from 'react';
import { WhereBuilder } from './WhereBuilder';
import { hasFilters, type FilterGroup } from './filters';
import {
  MAX_PARENT_DEPTH,
  METADATA_BROWSER_TID,
  type PathCrumb,
  type SalesforceField,
  type SalesforceObjectMetadata,
  type SalesforceRelationship,
  type SubquerySelection,
} from './types';

const TID = `${METADATA_BROWSER_TID}.detail`;

// Relationship names can be null (e.g. some polymorphic references); fall back to the
// FK field for a stable, unique testid/key.
const relKey = (r: SalesforceRelationship) => r.name ?? r.viaField ?? r.targetObject;

const CopyIcon = ({ text, testId }: { text: string; testId: string }) => (
  <button
    type="button"
    className="sfmb-iconbtn"
    title="Copy API name"
    onClick={() => void navigator.clipboard?.writeText(text)}
    data-testid={testId}
  >
    <FontAwesomeIcon icon={faCopy} />
  </button>
);

interface ObjectDetailPanelProps {
  connectionId: string;
  metadata: SalesforceObjectMetadata | undefined;
  loading: boolean;
  path: PathCrumb[];
  prefix: string;
  parentDepth: number;
  selectable: boolean; // false once we've navigated through a child (dot-path selection invalid)
  selectedFields: Set<string>;
  onToggleField: (dotPath: string) => void;
  onNavigateParent: (r: SalesforceRelationship) => void;
  onNavigateChild: (r: SalesforceRelationship) => void;
  onCrumb: (index: number) => void;
  canSubquery: boolean;
  subqueries: Record<string, SubquerySelection>;
  onToggleSubqueryField: (relName: string, targetObject: string, fieldApiName: string) => void;
  where: FilterGroup;
  onWhereChange: (root: FilterGroup) => void;
}

export function ObjectDetailPanel(props: ObjectDetailPanelProps) {
  const {
    connectionId,
    metadata,
    loading,
    path,
    prefix,
    parentDepth,
    selectable,
    selectedFields,
    onToggleField,
    onNavigateParent,
    onNavigateChild,
    onCrumb,
    canSubquery,
    subqueries,
    onToggleSubqueryField,
    where,
    onWhereChange,
  } = props;

  const [tab, setTab] = useState('fields');

  // Landing on a new object (drill into a parent, Explore a child, crumb nav) resets to Fields.
  const currentObjectKey = path.length > 0 ? path[path.length - 1].object : null;
  useEffect(() => {
    setTab('fields');
  }, [currentObjectKey]);

  if (path.length === 0) {
    return (
      <SCView
        flex1
        flexCol
        itemsCenter
        justifyCenter
        className="sfmb-panel sfmb-detail"
        DataTestId={TID}
      >
        <SCView textDesc>Select an object to view its fields and relationships.</SCView>
      </SCView>
    );
  }

  const current = path[path.length - 1];
  const dotPath = (apiName: string) => (prefix ? `${prefix}.${apiName}` : apiName);
  // The Filters tab only exists on the base object; fall back to Fields when we've navigated away.
  const effTab = tab === 'filters' && !canSubquery ? 'fields' : tab;

  return (
    <SCView flex1 flexCol className="sfmb-panel sfmb-detail" DataTestId={TID}>
      {/* PATH + parent depth */}
      <SCView flex justifyBetween itemsCenter px={3} py={2} borderB>
        <SCView flex itemsCenter gap={1} className="sfmb-mono" DataTestId={`${TID}.breadcrumb`}>
          <SCView textXs textDesc mr={1}>
            PATH
          </SCView>
          {path.map((c, i) => {
            const isLast = i === path.length - 1;
            return (
              <SCView key={`${i}-${c.object}`} flex itemsCenter gap={1}>
                {i > 0 && (
                  <SCView textDesc textXs className={c.isChild ? 'sfmb-arrow-child' : undefined}>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </SCView>
                )}
                <SCView
                  textPrimary={!isLast && !c.isChild}
                  textMain={isLast}
                  fontBold={isLast}
                  cursorPointer={!isLast}
                  className={c.isChild && !isLast ? 'sfmb-arrow-child' : undefined}
                  onClick={isLast ? undefined : () => onCrumb(i)}
                  DataTestId={`${TID}.crumb.${i}`}
                >
                  {c.isChild ? `←${c.label}` : c.label}
                </SCView>
              </SCView>
            );
          })}
        </SCView>
        <span className="sfmb-badge sfmb-badge-neutral">
          Parent depth {parentDepth}/{MAX_PARENT_DEPTH}
        </span>
      </SCView>

      {/* object title */}
      <SCView flex itemsCenter gap={2} px={3} py={2}>
        <SCView textLg fontBold>
          {current.label}
        </SCView>
        {metadata && (
          <span
            className={`sfmb-badge ${metadata.isCustom ? 'sfmb-badge-custom' : 'sfmb-badge-neutral'}`}
          >
            {metadata.isCustom ? 'Custom object' : 'Standard object'}
          </span>
        )}
        <span className="sfmb-badge sfmb-badge-type">{current.object}</span>
        <CopyIcon text={current.object} testId={`${TID}.copy-object`} />
      </SCView>

      {loading || !metadata ? (
        <SCLoading />
      ) : (
        <SCView flex1 flex flexCol style={{ minHeight: 0 }}>
          <SCView px={3}>
            <TabGroup
              Value={effTab}
              OnChange={setTab}
              SpaceUnder
              DataTestId={`${TID}.tab`}
              Tabs={[
                { Value: 'fields', Label: 'Fields', BadgeText: String(metadata.fields.length) },
                {
                  Value: 'relationships',
                  Label: 'Relationships',
                  BadgeText: String(
                    metadata.parentReferences.length + metadata.childRelationships.length,
                  ),
                },
                ...(canSubquery
                  ? [
                      {
                        Value: 'filters',
                        Label: 'Filters',
                        BadgeText: hasFilters(where) ? '•' : undefined,
                      },
                    ]
                  : []),
              ]}
            />
          </SCView>
          <SCView flex1 overflowYAuto style={{ minHeight: 0 }}>
            {effTab === 'fields' && (
              <FieldsTab
                fields={metadata.fields}
                dotPath={dotPath}
                selectable={selectable}
                selectedFields={selectedFields}
                onToggleField={onToggleField}
              />
            )}
            {effTab === 'relationships' && (
              <RelationshipsTab
                connectionId={connectionId}
                parents={metadata.parentReferences}
                childrens={metadata.childRelationships}
                parentDepth={parentDepth}
                onNavigateParent={onNavigateParent}
                onNavigateChild={onNavigateChild}
                canSubquery={canSubquery}
                baseLabel={path[0].label}
                onGoToBase={() => onCrumb(0)}
                subqueries={subqueries}
                onToggleSubqueryField={onToggleSubqueryField}
              />
            )}
            {effTab === 'filters' && canSubquery && (
              <SCView p={3}>
                <WhereBuilder fields={metadata.fields} root={where} onChange={onWhereChange} />
              </SCView>
            )}
          </SCView>
        </SCView>
      )}
    </SCView>
  );
}

function FieldsTab({
  fields,
  dotPath,
  selectable,
  selectedFields,
  onToggleField,
}: {
  fields: SalesforceField[];
  dotPath: (apiName: string) => string;
  selectable: boolean;
  selectedFields: Set<string>;
  onToggleField: (dotPath: string) => void;
}) {
  const [expandedPicklist, setExpandedPicklist] = useState<string | null>(null);
  if (fields.length === 0)
    return (
      <SCView p={3} textDesc>
        No fields.
      </SCView>
    );

  return (
    <SCView flexCol>
      {!selectable && (
        <SCView textXs textDesc p={2} style={{ background: 'var(--sc-bg-color-warning)' }}>
          Browsing a child relationship — fields here can’t be added as dot-paths. Query child data
          with a subquery from the base object instead.
        </SCView>
      )}
      {fields.map((f) => {
        const dp = dotPath(f.apiName);
        const checked = selectedFields.has(dp);
        const hasPicklist = !!f.picklistValues && f.picklistValues.length > 0;
        return (
          <SCView
            key={f.apiName}
            className="sfmb-field-row"
            DataTestId={`${TID}.field.${f.apiName}`}
          >
            <SCView flex itemsCenter gap={2} Component="label" className="sfmb-check">
              <input
                type="checkbox"
                checked={checked}
                disabled={!selectable}
                onChange={() => onToggleField(dp)}
                data-testid={`${TID}.field.${f.apiName}.select`}
              />
              <SCView fontBold>{f.label}</SCView>
              <SCView textXs textDesc className="sfmb-mono">
                {f.apiName}
              </SCView>
            </SCView>
            <SCView flex itemsCenter flexWrap gap={1} mt={1} ml={4}>
              <span className="sfmb-badge sfmb-badge-type">{f.type}</span>
              {f.filterable && (
                <span className="sfmb-flag" title="Usable in a WHERE clause">
                  WHERE
                </span>
              )}
              {f.sortable && (
                <span className="sfmb-flag" title="Usable in ORDER BY">
                  ORDER&nbsp;BY
                </span>
              )}
              {f.writable && (
                <span className="sfmb-flag" title="Writable / updateable">
                  Writable
                </span>
              )}
              {f.calculated && (
                <span className="sfmb-flag sfmb-flag-formula" title="Formula / calculated field">
                  Formula
                </span>
              )}
              {hasPicklist && (
                <SCView
                  textLink
                  textXs
                  cursorPointer
                  onClick={() =>
                    setExpandedPicklist(expandedPicklist === f.apiName ? null : f.apiName)
                  }
                  DataTestId={`${TID}.field.${f.apiName}.picklist`}
                >
                  {expandedPicklist === f.apiName
                    ? 'Hide values'
                    : `Show ${f.picklistValues!.length} values`}
                </SCView>
              )}
              <SCView ml="auto">
                <CopyIcon text={f.apiName} testId={`${TID}.field.${f.apiName}.copy`} />
              </SCView>
            </SCView>
            {hasPicklist && expandedPicklist === f.apiName && (
              <SCView flex flexWrap gap={1} mt={2} ml={4}>
                {f.picklistValues!.map((p) => (
                  <span key={p.value} className="sfmb-pill" title={p.value}>
                    {p.label || p.value}
                  </span>
                ))}
              </SCView>
            )}
          </SCView>
        );
      })}
    </SCView>
  );
}

function RelationshipsTab({
  connectionId,
  parents,
  childrens,
  parentDepth,
  onNavigateParent,
  onNavigateChild,
  canSubquery,
  baseLabel,
  onGoToBase,
  subqueries,
  onToggleSubqueryField,
}: {
  connectionId: string;
  parents: SalesforceRelationship[];
  childrens: SalesforceRelationship[];
  parentDepth: number;
  onNavigateParent: (r: SalesforceRelationship) => void;
  onNavigateChild: (r: SalesforceRelationship) => void;
  canSubquery: boolean;
  baseLabel: string;
  onGoToBase: () => void;
  subqueries: Record<string, SubquerySelection>;
  onToggleSubqueryField: (relName: string, targetObject: string, fieldApiName: string) => void;
}) {
  const atMaxDepth = parentDepth >= MAX_PARENT_DEPTH;

  return (
    <SCView flex gap={4} p={3} flexWrap>
      {/* Parent references */}
      <SCView flex1 flexCol gap={2} style={{ minWidth: 260 }}>
        <SCView flex itemsCenter gap={2}>
          <SCView fontBold textSm>
            Parent references
          </SCView>
          <span className="sfmb-badge sfmb-badge-neutral">{parents.length}</span>
        </SCView>
        <SCView textXs textDesc>
          Drill into a reference to walk its relationship name into a dot-path. Selected fields
          become <span className="sfmb-mono">Rel.Field</span>.
        </SCView>
        {parents.length === 0 && <SCView textDesc>No parent references.</SCView>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          {parents.map((r, i) => {
            const key = relKey(r);
            const canDrill = !!r.name && !atMaxDepth;
            return (
              <SCView
                key={`p-${key}-${i}`}
                className="sfmb-rel-card parent"
                DataTestId={`${TID}.parent.${key}`}
              >
                <SCView flex justifyBetween itemsCenter gap={2}>
                  <SCView fontBold>{r.name ?? '(unnamed)'}</SCView>
                  <CopyIcon text={r.name ?? r.viaField} testId={`${TID}.parent.${key}.copy`} />
                </SCView>
                <SCView textXs textDesc mt={1}>
                  via <span className="sfmb-mono">{r.viaField}</span> ·{' '}
                  <span className="sfmb-arrow-parent">→ {r.targetObject}</span>
                </SCView>
                <SCView mt={2}>
                  <Button
                    Label={`${r.targetObject} →`}
                    Disabled={!canDrill}
                    OnClick={() => onNavigateParent(r)}
                    Class="eone-ui-button-container-secondary"
                    Title={
                      atMaxDepth
                        ? `Maximum parent depth (${MAX_PARENT_DEPTH}) reached`
                        : !r.name
                          ? 'This reference has no relationship name (cannot dot-path)'
                          : `Drill into ${r.targetObject} as a dot-path`
                    }
                    DataTestId={`${TID}.parent.${key}.drill`}
                  />
                </SCView>
              </SCView>
            );
          })}
        </div>
      </SCView>

      {/* Child relationships */}
      <div style={{ minWidth: 260, flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SCView flex itemsCenter gap={2}>
          <SCView fontBold textSm>
            Child relationships
          </SCView>
          <span className="sfmb-badge sfmb-badge-neutral">{childrens.length}</span>
        </SCView>
        {canSubquery ? (
          <SCView textXs textDesc>
            Expand to add a <span className="sfmb-mono">(SELECT … FROM Rel)</span> subquery — tick
            the child’s fields and it drops into the query.
          </SCView>
        ) : (
          <SCView
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
              padding: 10,
              border: '1px solid var(--border-subtle)',
              borderRadius: 3,
            }}
          >
            <SCView textXs textDesc>
              These are children of the current object. Subqueries can only be added from the base
              object (<span className="sfmb-mono">{baseLabel}</span>) — go there, then use “Add
              fields” on its child relationships.
            </SCView>
            <SCView>
              <Button
                Label={`← Back to ${baseLabel}`}
                OnClick={onGoToBase}
                Class="eone-ui-button-container-secondary"
                DataTestId={`${TID}.go-to-base`}
              />
            </SCView>
          </SCView>
        )}
        {childrens.length === 0 && <SCView textDesc>No child relationships.</SCView>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          {childrens.map((r, i) => {
            const key = relKey(r);
            if (canSubquery && r.name) {
              return (
                <ChildSubqueryCard
                  key={`c-${key}-${i}`}
                  connectionId={connectionId}
                  rel={r}
                  relName={r.name}
                  testKey={key}
                  selected={subqueries[r.name]?.fields ?? []}
                  onToggleField={onToggleSubqueryField}
                />
              );
            }
            return (
              <SCView
                key={`c-${key}-${i}`}
                className="sfmb-rel-card child"
                DataTestId={`${TID}.child.${key}`}
              >
                <SCView flex justifyBetween itemsCenter gap={2}>
                  <SCView fontBold className="sfmb-arrow-child" truncate>
                    ← {r.name ?? '(unnamed)'}
                  </SCView>
                  <button
                    type="button"
                    className="sfmb-iconbtn"
                    onClick={() => onNavigateChild(r)}
                    title={`Explore ${r.targetObject} — browse its fields and relationships`}
                    data-testid={`${TID}.child.${key}.explore`}
                  >
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                  </button>
                </SCView>
                <SCView textXs textDesc mt={1}>
                  from{' '}
                  <span className="sfmb-mono">
                    {r.targetObject}.{r.viaField}
                  </span>
                  {!canSubquery && ' · subquery from the base object only'}
                </SCView>
              </SCView>
            );
          })}
        </div>
      </div>
    </SCView>
  );
}

// One child relationship, expandable into a field picker for its (SELECT ... FROM Rel) subquery.
// The child object's fields are fetched lazily (and cached) the first time the card is expanded.
function ChildSubqueryCard({
  connectionId,
  rel,
  relName,
  testKey,
  selected,
  onToggleField,
}: {
  connectionId: string;
  rel: SalesforceRelationship;
  relName: string;
  testKey: string;
  selected: string[];
  onToggleField: (relName: string, targetObject: string, fieldApiName: string) => void;
}) {
  const [open, setOpen] = useState(false);

  const { data: childMeta, isLoading } = useQuery({
    queryKey: [
      'salesforce',
      'metadata-browser',
      'sobject-metadata',
      connectionId,
      rel.targetObject,
    ],
    enabled: open && !!connectionId,
    queryFn: () =>
      request<SalesforceObjectMetadata>({
        url: `/salesforce/get-sobject-metadata/${connectionId}/${rel.targetObject}`,
      }),
  });

  return (
    <SCView className="sfmb-rel-card child" DataTestId={`${TID}.child.${testKey}`}>
      <SCView flex justifyBetween itemsCenter gap={2}>
        <SCView fontBold className="sfmb-arrow-child">
          ← {relName}
        </SCView>
        <SCView flex itemsCenter gap={2}>
          {selected.length > 0 && (
            <span className="sfmb-badge sfmb-badge-type">{selected.length}</span>
          )}
          <button
            type="button"
            className="sfmb-iconbtn"
            onClick={() => setOpen((v) => !v)}
            title={
              open
                ? 'Hide fields'
                : selected.length > 0
                  ? `Edit subquery fields (${selected.length})`
                  : 'Add subquery fields'
            }
            data-testid={`${TID}.child.${testKey}.toggle`}
          >
            <FontAwesomeIcon
              icon={open ? faChevronUp : selected.length > 0 ? faPenToSquare : faPlus}
            />
          </button>
        </SCView>
      </SCView>
      <SCView textXs textDesc mt={1}>
        from{' '}
        <span className="sfmb-mono">
          {rel.targetObject}.{rel.viaField}
        </span>
      </SCView>
      {open && (
        <SCView flexCol mt={2} className="sfmb-child-fields">
          {isLoading && <SCLoading />}
          {!isLoading && childMeta && childMeta.fields.length === 0 && (
            <SCView textDesc>No fields.</SCView>
          )}
          {!isLoading &&
            childMeta &&
            childMeta.fields.map((f) => (
              <SCView
                key={f.apiName}
                flex
                itemsCenter
                gap={2}
                Component="label"
                py={1}
                className="sfmb-check"
                DataTestId={`${TID}.child.${testKey}.field.${f.apiName}`}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(f.apiName)}
                  onChange={() => onToggleField(relName, rel.targetObject, f.apiName)}
                />
                <SCView>{f.label}</SCView>
                <SCView textXs textDesc className="sfmb-mono">
                  {f.apiName}
                </SCView>
              </SCView>
            ))}
        </SCView>
      )}
    </SCView>
  );
}
